import React, { useState, useEffect } from 'react';
import { loadingScreenAPI } from '../services/api';
import styles from '../styles/LoadingScreen.module.css';

const LOADING_DATA = [
  { text: 'Building your financial confidence...', image: 'Building Your Financial Confidence.png' },
  { text: 'Empowering your investment journey...', image: 'Empowering Your Investment Journey.png' },
  { text: 'Your wealth, your way...', image: 'Your Wealth, Your Way.png' },
  { text: 'Sophia builds your personalised path...', image: 'Creating Your Personalized Path.png' },
  { text: 'Investing in your future...', image: 'Investing in Your Future.png' },
  { text: 'Growing your financial independence...', image: 'Growing Your Financial Independence.png' }
];

// Store last used image globally to prevent consecutive duplicates
let lastUsedImage = null;

/**
 * Unified Loading Screen Component
 *
 * Props:
 * - text: Loading text to display (optional, will fetch personalized content from API if not provided)
 * - variant: 'dots' (animated dots), 'spinner' (CSS spinner), or 'progress' (progress bar) - default: 'spinner'
 * - fullScreen: Take full screen height (default: false)
 * - fetchPersonalized: Whether to fetch personalized facts/stats from API (default: true)
 * - progress: Progress percentage (0-100) for 'progress' variant (default: 0)
 */
function LoadingScreen({ text, variant = 'spinner', fullScreen = false, fetchPersonalized = true, progress = 0 }) {
  const [loadingData] = useState(() => {
    // Pick random loading data on mount, avoiding the last used image
    let availableData = LOADING_DATA;

    // Filter out the last used image if we have more than one option
    if (lastUsedImage && LOADING_DATA.length > 1) {
      availableData = LOADING_DATA.filter(data => data.image !== lastUsedImage);
    }

    const selectedData = availableData[Math.floor(Math.random() * availableData.length)];
    lastUsedImage = selectedData.image;

    return selectedData;
  });

  const [personalizedContent, setPersonalizedContent] = useState(null);
  const [contentType, setContentType] = useState(null);

  // Fetch personalized content from API
  useEffect(() => {
    if (!fetchPersonalized || text) {
      return; // Skip if explicit text provided or fetch disabled
    }

    async function fetchContent() {
      try {
        const data = await loadingScreenAPI.getPersonalizedContent();
        setPersonalizedContent(data.message);
        setContentType(data.type); // 'fact' or 'stat'
        console.log('[Loading Screen] Fetched personalized content:', data.type, data.message);
      } catch (error) {
        console.error('[Loading Screen] Failed to fetch personalized content:', error);
        // Silently fall back to random messages
      }
    }

    fetchContent();
  }, [text, fetchPersonalized]);

  const displayText = text || personalizedContent || loadingData.text;

  // Apply special styling for personalized content
  const textClassName = contentType === 'fact'
    ? `${styles.text} ${styles.personalizedFact}`
    : styles.text;

  return (
    <div className={`${styles.loadingScreen} ${fullScreen ? styles.fullScreen : ''}`}>
      {/* Animated Background Particles */}
      <div className={styles.particlesContainer}>
        <div className={styles.particle}></div>
        <div className={styles.particle}></div>
        <div className={styles.particle}></div>
        <div className={styles.particle}></div>
        <div className={styles.particle}></div>
        <div className={styles.particle}></div>
      </div>

      <div className={styles.content}>
        <div className={textClassName}>{displayText}</div>

        {variant === 'dots' ? (
          <div className={styles.dotsWrapper}>
            <div className={styles.dots}>
              <div className={styles.dot}></div>
              <div className={styles.dot}></div>
              <div className={styles.dot}></div>
            </div>
          </div>
        ) : variant === 'progress' ? (
          <div className={styles.progressBarContainer}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressBarFill}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              ></div>
            </div>
            <div className={styles.progressText}>{Math.round(progress)}%</div>
          </div>
        ) : (
          <div className={styles.spinner}></div>
        )}
      </div>
    </div>
  );
}

export default LoadingScreen;
